/**
 * @file ChatConfigMenu.jsx
 * @description Menú de configuración del chat estilo Claude Web
 *
 * Características:
 * - Dropdown menu con switches para opciones
 * - Item con submenu para "Usar estilo"
 * - Todas las opciones deshabilitadas por ahora (funcionalidades futuras)
 * - Soporte light/dark mode automático
 * - Integrado en ChatInput junto a botones de archivo/audio
 */

import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Switch,
  Divider,
  Typography,
  Box,
  Tooltip,
  alpha,
  useTheme
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import SearchIcon from '@mui/icons-material/Search';
import PsychologyIcon from '@mui/icons-material/Psychology';
import StyleIcon from '@mui/icons-material/Style';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const ChatConfigMenu = ({ disabled = false }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [styleMenuAnchor, setStyleMenuAnchor] = useState(null);
  const open = Boolean(anchorEl);
  const styleMenuOpen = Boolean(styleMenuAnchor);

  // Estados de las opciones (por ahora todos false, ya que están deshabilitadas)
  const [config, setConfig] = useState({
    research: false,
    extendedThinking: false,
    style: 'default'
  });

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setStyleMenuAnchor(null);
  };

  const handleStyleMenuOpen = (event) => {
    setStyleMenuAnchor(event.currentTarget);
  };

  const handleStyleMenuClose = () => {
    setStyleMenuAnchor(null);
  };

  // Handlers para futuros cambios (cuando se activen)
  const handleResearchToggle = () => {
    console.log('[ChatConfig] Research toggle - Currently disabled');
  };

  const handleExtendedThinkingToggle = () => {
    console.log('[ChatConfig] Extended thinking toggle - Currently disabled');
  };

  const handleStyleSelect = (style) => {
    console.log('[ChatConfig] Style selected:', style, '- Currently disabled');
    handleStyleMenuClose();
    handleClose();
  };

  return (
    <>
      <Tooltip title="Configuración del chat" arrow>
        <IconButton
          onClick={handleClick}
          disabled={disabled}
          aria-label="configuración del chat"
          aria-controls={open ? 'chat-config-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          sx={{
            width: { xs: 32, md: 40 },
            height: { xs: 32, md: 40 },
            backgroundColor: 'transparent',
            border: {
              xs: 'none',
              md: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
            },
            borderRadius: '50%',
            color: theme.palette.primary.main,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              borderColor: { xs: 'transparent', md: alpha(theme.palette.primary.main, 0.2) }
            },
            '&:active': {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              borderColor: alpha(theme.palette.primary.main, 0.3),
              transform: 'scale(0.98)'
            },
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: '2px'
            },
            '&.Mui-disabled': {
              backgroundColor: 'transparent',
              borderColor: theme.palette.divider,
              opacity: 0.5
            }
          }}
        >
          <TuneIcon />
        </IconButton>
      </Tooltip>

      <Menu
        id="chat-config-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock={true}
        MenuListProps={{
          'aria-labelledby': 'chat-config-button'
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        PaperProps={{
          sx: {
            minWidth: 280,
            maxWidth: 300,
            mb: 1,
            boxShadow: 3,
            borderRadius: 2,
            '& .MuiMenuItem-root': {
              py: 0.75,
              px: 1.5,
              minHeight: 48
            }
          }
        }}
      >
        <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '0.8rem'
            }}
          >
            Opciones del chat
          </Typography>
        </Box>

        <Divider />

        <MenuItem
          onClick={handleResearchToggle}
          disabled={true}
          sx={{
            opacity: 0.6,
            cursor: 'not-allowed !important',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            <ListItemIcon sx={{ minWidth: 'auto' }}>
              <SearchIcon fontSize="small" sx={{ color: 'text.primary' }} />
            </ListItemIcon>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: 'text.primary',
                  fontSize: '0.8rem',
                  lineHeight: 1.3
                }}
              >
                Investigación
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  display: 'block',
                  lineHeight: 1.2
                }}
              >
                Buscar información actualizada
              </Typography>
            </Box>
          </Box>
          <Switch
            edge="end"
            checked={config.research}
            disabled={true}
            size="small"
            inputProps={{
              'aria-label': 'activar investigación'
            }}
          />
        </MenuItem>

        <MenuItem
          onClick={handleExtendedThinkingToggle}
          disabled={true}
          sx={{
            opacity: 0.6,
            cursor: 'not-allowed !important',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            <ListItemIcon sx={{ minWidth: 'auto' }}>
              <PsychologyIcon fontSize="small" sx={{ color: 'text.primary' }} />
            </ListItemIcon>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: 'text.primary',
                  fontSize: '0.8rem',
                  lineHeight: 1.3
                }}
              >
                Pensamiento extendido
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  display: 'block',
                  lineHeight: 1.2
                }}
              >
                Análisis más profundo
              </Typography>
            </Box>
          </Box>
          <Switch
            edge="end"
            checked={config.extendedThinking}
            disabled={true}
            size="small"
            inputProps={{
              'aria-label': 'activar pensamiento extendido'
            }}
          />
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          onClick={handleStyleMenuOpen}
          disabled={true}
          sx={{
            opacity: 0.6,
            cursor: 'not-allowed !important',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            <ListItemIcon sx={{ minWidth: 'auto' }}>
              <StyleIcon fontSize="small" sx={{ color: 'text.primary' }} />
            </ListItemIcon>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: 'text.primary',
                  fontSize: '0.8rem',
                  lineHeight: 1.3
                }}
              >
                Usar estilo
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  display: 'block',
                  lineHeight: 1.2
                }}
              >
                Tono de respuesta
              </Typography>
            </Box>
          </Box>
          <ChevronRightIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.68rem',
              fontStyle: 'italic',
              lineHeight: 1.3
            }}
          >
            Estas funciones estarán disponibles próximamente
          </Typography>
        </Box>
      </Menu>

      <Menu
        anchorEl={styleMenuAnchor}
        open={styleMenuOpen}
        onClose={handleStyleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            boxShadow: 3,
            borderRadius: 2
          }
        }}
      >
        <MenuItem onClick={() => handleStyleSelect('default')} disabled>
          <ListItemText
            primary="Por defecto"
            primaryTypographyProps={{
              sx: { color: 'text.primary', fontSize: '0.875rem' }
            }}
          />
        </MenuItem>
        <MenuItem onClick={() => handleStyleSelect('formal')} disabled>
          <ListItemText
            primary="Formal"
            primaryTypographyProps={{
              sx: { color: 'text.primary', fontSize: '0.875rem' }
            }}
          />
        </MenuItem>
        <MenuItem onClick={() => handleStyleSelect('casual')} disabled>
          <ListItemText
            primary="Casual"
            primaryTypographyProps={{
              sx: { color: 'text.primary', fontSize: '0.875rem' }
            }}
          />
        </MenuItem>
        <MenuItem onClick={() => handleStyleSelect('technical')} disabled>
          <ListItemText
            primary="Técnico"
            primaryTypographyProps={{
              sx: { color: 'text.primary', fontSize: '0.875rem' }
            }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default ChatConfigMenu;
